import { useCallback, useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import { Play } from "lucide-react";

export const Preloader = () => {
  const [phase, setPhase] = useState("video"); // video | leaving | done
  const [needsTap, setNeedsTap] = useState(false);
  const videoRef = useRef(null);

  const finish = useCallback(() => {
    const video = videoRef.current;
    if (video && !video.paused) video.pause();
    window.__cobIntroEnded = true;
    window.dispatchEvent(new CustomEvent("cob:intro-ended"));
    setPhase("leaving");
  }, []);

  useEffect(() => {
    document.body.style.overflow = "hidden";
    const video = videoRef.current;
    if (video) {
      video.muted = false;
      video.play().catch(() => setNeedsTap(true));
      video.addEventListener("ended", finish);
      video.addEventListener("error", finish);
    }
    const fallback = setTimeout(finish, 20000);
    return () => {
      clearTimeout(fallback);
      video?.removeEventListener("ended", finish);
      video?.removeEventListener("error", finish);
      document.body.style.overflow = "";
    };
  }, [finish]);

  useEffect(() => {
    if (phase === "video") return;
    const t = setTimeout(() => {
      setPhase("done");
      document.body.style.overflow = "";
    }, 900);
    return () => clearTimeout(t);
  }, [phase]);

  if (phase === "done") return null;

  const leaving = phase === "leaving";

  const tapToPlay = () => {
    const video = videoRef.current;
    if (!video) return;
    video.muted = false;
    video
      .play()
      .then(() => setNeedsTap(false))
      .catch(() => {});
  };

  return (
    <motion.div
      data-testid="preloader"
      className="fixed inset-0 z-[100] overflow-hidden bg-black"
      initial={{ opacity: 1 }}
      animate={{ opacity: leaving ? 0 : 1, scale: leaving ? 1.06 : 1 }}
      transition={{ duration: 0.85, ease: "easeInOut" }}
      style={{ pointerEvents: leaving ? "none" : "auto" }}
    >
      <video
        ref={videoRef}
        src="/logo-anim.mp4"
        playsInline
        preload="auto"
        data-testid="preloader-video"
        className="h-full w-full object-contain"
      />

      {needsTap && !leaving && (
        <div className="absolute inset-0 flex flex-col items-center justify-center gap-4 bg-cob-dark/70 backdrop-blur-sm">
          <button
            type="button"
            data-testid="preloader-play"
            onClick={tapToPlay}
            className="btn-press inline-flex items-center gap-3 rounded-full bg-cob-gold px-8 py-4 text-sm font-bold text-cob-dark shadow-[0_18px_45px_-12px_rgba(213,166,66,0.6)] transition-colors hover:bg-white"
          >
            <Play className="h-5 w-5" />
            Assistir com som
          </button>
          <p className="text-[12px] font-medium text-white/70">Toque para ver a apresentação da Clínica COB</p>
        </div>
      )}

      {!leaving && (
        <button
          type="button"
          data-testid="preloader-skip"
          onClick={finish}
          className="absolute bottom-6 right-6 rounded-full border border-white/25 px-5 py-2.5 text-[12px] font-semibold text-white/80 backdrop-blur-sm transition-colors hover:bg-white/10 hover:text-white"
        >
          Pular introdução
        </button>
      )}
    </motion.div>
  );
};
